<template>
  <button v-if="isButtonFormat"
    :class="buttonClass"
    :aria-label="buttonAriaLabel"
    :title="buttonTitle"
    :style="buttonStyle"
    :disabled="disabled"
    @click.stop="handleClick"
  >
    <div class="button-content">
      <i v-if="showIcon && buttonIcon" :class="buttonIcon" :style="iconStyle" aria-hidden="true"></i>
      <span v-if="showText" class="button-text">{{ buttonText || 'Action' }}</span>
    </div>
  </button>

  <a v-else-if="buttonFormat === 'download-link'"
    :href="downloadUrl"
    class="download-link"
    :title="buttonTitle"
    :aria-label="buttonAriaLabel"
    target="_blank"
  >
    {{ displayDownloadLink }}
  </a>

  <span v-else>-</span>
</template>

<script>
// Presets for common button actions with default button styling
const DEFAULT_ACTION_PRESETS = {
  edit: {
    icon: 'fas fa-pencil-alt',
    variant: 'primary',
    format: 'icon',
  },
  delete: {
    icon: 'fas fa-trash',
    variant: 'danger',
    format: 'icon',
  },
  open: {
    icon: 'fas fa-external-link-alt',
    variant: 'primary',
    format: 'icon',
  },
  view: {
    icon: 'fas fa-eye',
    variant: 'primary',
    format: 'icon',
  },
  'download-file': {
    icon: 'fas fa-download',
    text: 'Download',
    variant: 'primary',
    format: 'icon-text',
  },
  'download-link': {
    variant: 'action-unstyled',
    format: 'download-link',
  },
  approve: {
    icon: 'fas fa-thumbs-up',
    variant: 'success',
    format: 'icon',
  },
  reject: {
    icon: 'fas fa-thumbs-down',
    variant: 'outlined-danger',
    format: 'icon',
  },
  refer: {
    icon: 'fas fa-thumbs-down',
    variant: 'warning',
    format: 'icon',
  },
  archive: {
    variant: 'warning',
    format: 'text',
    text: 'Archive',
  },
  unarchive: {
    variant: 'primary',
    format: 'text',
    text: 'Unarchive',
  },
  enable: {
    variant: 'success',
    format: 'text',
    text: 'Enable',
  },
  disable: {
    variant: 'danger',
    format: 'text',
    text: 'Disable',
  },
  schedule: {
    icon: 'fas fa-calendars',
    variant: 'success',
    format: 'icon',
  },
  sync: {
    icon: 'fas fa-sync',
    variant: 'primary',
    format: 'icon',
  },
  'warning-unstyled': {
    icon: 'fas fa-exclamation-triangle',
    variant: 'action-unstyled',
    format: 'icon',
  },
  'edit-unstyled': {
    icon: 'fas fa-edit',
    variant: 'action-unstyled',
    format: 'icon',
  },
  'delete-unstyled': {
    icon: 'fas fa-trash-alt',
    variant: 'action-unstyled',
    format: 'icon',
    defaultColor: 'var(--kate-danger)',
  },
  'loading-unstyled': {
    icon: 'fas fa-spinner fa-spin',
    variant: 'action-unstyled',
    format: 'icon',
    defaultColor: 'var(--kate-primary)',
  },
  'check-unstyled': {
    icon: 'fas fa-check',
    variant: 'action-unstyled',
    format: 'icon',
  },
  'play-unstyled': {
    icon: 'fas fa-play-circle',
    variant: 'action-unstyled',
    format: 'icon',
    defaultColor: '#5cfff3',
  },
  'transcript-unstyled': {
    icon: 'fas fa-closed-captioning',
    variant: 'action-unstyled',
    format: 'icon',
  },
  user: {
    icon: 'fas fa-user',
    variant: 'primary',
    format: 'icon',
  },
};

export default {
  props: {
    preset: {
      type: String,
      default: undefined,
      validator: value => Object.keys(DEFAULT_ACTION_PRESETS).includes(value),
    },
    format: {
      type: String,
      validator: value => ['icon', 'text', 'icon-text', 'download-link'].includes(value),
    },
    variant: {
      type: String,
      validator: value => ['primary', 'secondary', 'danger', 'success', 'warning', 'outlined', 'outlined-danger', 'link', 'action-unstyled', 'default'].includes(value),
    },
    text: {
      type: String,
      required: false,
    },
    icon: {
      type: String,
      required: false,
    },
    customFill: {
      type: String,
      required: false,
    },
    customColor: {
      type: String,
      required: false,
    },
    customSize: {
      type: String,
      required: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    ariaLabel: {
      type: String,
      required: true,
    },
    downloadUrl: {
      type: String,
      required: false,
    },
  },

  computed: {
    buttonFormat() {
      if (this.format) {
        return this.format;
      }
      if (this.preset && DEFAULT_ACTION_PRESETS[this.preset]) {
        return DEFAULT_ACTION_PRESETS[this.preset].format;
      }
      return 'icon';
    },

    buttonIcon() {
      if (this.icon) {
        return this.icon;
      }
      if (this.preset && DEFAULT_ACTION_PRESETS[this.preset]) {
        return DEFAULT_ACTION_PRESETS[this.preset].icon;
      }
      return '';
    },

    buttonText() {
      if (this.text) {
        return this.text;
      }
      if (this.preset) {
        const preset = DEFAULT_ACTION_PRESETS[this.preset];
        if (preset && preset.text) {
          return preset.text;
        }
      }
      return '';
    },

    buttonAriaLabel() {
      if (this.ariaLabel) {
        return this.ariaLabel;
      }
      return this.capitaliseText(this.text || this.preset || 'Button');
    },

    buttonTitle() {
      if (this.title) {
        return this.title;
      }
      return this.capitaliseText(this.text || this.preset || 'Button');
    },

    buttonVariant() {
      if (this.variant) {
        return this.variant;
      }
      if (this.preset && DEFAULT_ACTION_PRESETS[this.preset]) {
        return DEFAULT_ACTION_PRESETS[this.preset].variant;
      }
      return 'primary';
    },

    buttonClass() {
      return ['btn', `btn-${this.buttonVariant}`, `${this.buttonFormat}`];
    },

    buttonStyle() {
      return {
        'background-color': this.customFill,
        color: this.customColor,
      };
    },

    iconStyle() {
      const styles = {};
      if (this.customColor) {
        styles.color = this.customColor;
      } else if (this.preset && DEFAULT_ACTION_PRESETS[this.preset]?.defaultColor) {
        styles.color = DEFAULT_ACTION_PRESETS[this.preset].defaultColor;
      }
      if (this.customSize) {
        styles.fontSize = this.customSize;
      } else if (this.preset && DEFAULT_ACTION_PRESETS[this.preset]?.defaultSize) {
        styles.fontSize = DEFAULT_ACTION_PRESETS[this.preset].defaultSize;
      }
      return styles;
    },

    isButtonFormat() {
      return ['icon', 'icon-text', 'text'].includes(this.buttonFormat);
    },

    showIcon() {
      return ['icon', 'icon-text'].includes(this.buttonFormat);
    },

    showText() {
      return ['text', 'icon-text'].includes(this.buttonFormat);
    },

    displayDownloadLink() {
      if (this.downloadUrl.length < 40) {
        return this.downloadUrl;
      }
      const startLength = 20;
      const endLength = 10;
      const ellipsis = '...';
      const start = this.downloadUrl.slice(0, startLength);
      const end = this.downloadUrl.slice(-endLength);
      return start + ellipsis + end;
    },
  },

  methods: {
    capitaliseText(str) {
      if (!str) {
        return '';
      }
      return str.charAt(0).toUpperCase() + str.slice(1);
    },

    downloadFile(downloadUrl) {
      window.open(downloadUrl, '_blank');
    },

    handleClick(event) {
      if (this.preset === 'download-file' && this.downloadUrl) {
        this.downloadFile(this.downloadUrl);
      } else {
        this.$emit('button-click', event);
      }
    },
  },
};
</script>
